import { useState } from "react";
import { Check, Sparkles } from "lucide-react";
import {
  CHARACTER_GROUPS,
  SCENARIOS,
  TONES,
  AGE_GROUPS,
  LENGTHS,
} from "../lib/storyOptions";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const MAX_CHARACTERS = 3;

function SectionTitle({ children, step }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400/15 text-amber-300 font-bold text-sm border border-amber-400/30">
        {step}
      </span>
      <h2 className="text-2xl sm:text-3xl font-semibold text-slate-100 font-[Fredoka]">
        {children}
      </h2>
    </div>
  );
}

function ChipCard({ active, onClick, emoji, label, hint, testId, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={`option-card relative text-left rounded-2xl border p-4 sm:p-5 backdrop-blur-md
        ${
          active
            ? "bg-amber-400/10 border-amber-400 ring-2 ring-amber-400/60 shadow-[0_0_25px_rgba(251,191,36,0.25)]"
            : "bg-slate-800/40 border-white/10 hover:border-amber-400/40 hover:bg-slate-800/60"
        }
        ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <div className="flex items-start gap-3">
        <span className="text-3xl">{emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-100 text-base leading-snug">
            {label}
          </div>
          {hint && (
            <div className="text-xs text-slate-400 mt-1 leading-snug">{hint}</div>
          )}
        </div>
        {active && (
          <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-400 text-slate-900">
            <Check className="w-4 h-4" />
          </span>
        )}
      </div>
    </button>
  );
}

export default function StoryConfigurator({ onGenerate, loading }) {
  const [characters, setCharacters] = useState([]); // array of {id,label,emoji}
  const [scenario, setScenario] = useState(null);
  const [tone, setTone] = useState("calm");
  const [age, setAge] = useState("4-6");
  const [length, setLength] = useState("normal");
  const [extra, setExtra] = useState("");

  const toggleCharacter = (item) => {
    setCharacters((prev) => {
      const exists = prev.find((c) => c.id === item.id);
      if (exists) return prev.filter((c) => c.id !== item.id);
      if (prev.length >= MAX_CHARACTERS) return prev;
      return [...prev, item];
    });
  };

  const canSubmit = characters.length > 0 && !!scenario;

  const submit = () => {
    if (!canSubmit) return;
    onGenerate({
      characters: characters.map((c) => c.label),
      scenario: scenario?.label,
      tone,
      age_group: age,
      length,
      extra: extra.trim() || null,
      language: "es",
    });
  };

  return (
    <div className="space-y-12">
      {/* CHARACTERS */}
      <section data-testid="section-characters">
        <SectionTitle step={1}>
          Elige los personajes{" "}
          <span className="text-sm font-normal text-slate-400 ml-1">
            (hasta {MAX_CHARACTERS})
          </span>
        </SectionTitle>

        <div className="space-y-6">
          {CHARACTER_GROUPS.map((group) => (
            <div key={group.id}>
              <div className="text-slate-300 mb-3 text-sm uppercase tracking-wider">
                <span className="mr-2">{group.emoji}</span>
                {group.label}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {group.items.map((item) => {
                  const active = !!characters.find((c) => c.id === item.id);
                  const disabled =
                    !active && characters.length >= MAX_CHARACTERS;
                  return (
                    <ChipCard
                      key={item.id}
                      active={active}
                      disabled={disabled}
                      emoji={item.emoji}
                      label={item.label}
                      onClick={() => toggleCharacter(item)}
                      testId={`character-${item.id}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {characters.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="selected-characters">
            {characters.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-200 px-3 py-1 text-sm"
              >
                <span>{c.emoji}</span>
                {c.label}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* SCENARIO */}
      <section data-testid="section-scenarios">
        <SectionTitle step={2}>¿Dónde transcurre?</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {SCENARIOS.map((s) => (
            <ChipCard
              key={s.id}
              emoji={s.emoji}
              label={s.label}
              active={scenario?.id === s.id}
              onClick={() => setScenario(s)}
              testId={`scenario-${s.id}`}
            />
          ))}
        </div>
      </section>

      {/* TONE */}
      <section data-testid="section-tone">
        <SectionTitle step={3}>Tono del cuento</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TONES.map((t) => (
            <ChipCard
              key={t.id}
              emoji={t.emoji}
              label={t.label}
              hint={t.hint}
              active={tone === t.id}
              onClick={() => setTone(t.id)}
              testId={`tone-${t.id}`}
            />
          ))}
        </div>
      </section>

      {/* AGE */}
      <section data-testid="section-age">
        <SectionTitle step={4}>Edad del niño</SectionTitle>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {AGE_GROUPS.map((a) => (
            <ChipCard
              key={a.id}
              emoji={a.emoji}
              label={a.label}
              hint={a.hint}
              active={age === a.id}
              onClick={() => setAge(a.id)}
              testId={`age-${a.id}`}
            />
          ))}
        </div>
      </section>

      {/* LENGTH */}
      <section data-testid="section-length">
        <SectionTitle step={5}>Longitud</SectionTitle>
        <div className="grid grid-cols-3 gap-3">
          {LENGTHS.map((l) => (
            <ChipCard
              key={l.id}
              emoji={l.emoji}
              label={l.label}
              hint={l.hint}
              active={length === l.id}
              onClick={() => setLength(l.id)}
              testId={`length-${l.id}`}
            />
          ))}
        </div>
      </section>

      {/* EXTRA */}
      <section data-testid="section-extra">
        <SectionTitle step={6}>
          Algo especial{" "}
          <span className="text-sm font-normal text-slate-400 ml-1">(opcional)</span>
        </SectionTitle>
        <Label htmlFor="extra-input" className="sr-only">
          Detalle especial
        </Label>
        <Textarea
          id="extra-input"
          data-testid="extra-input"
          placeholder="Ej: incluye el nombre Lucía y su peluche Pelusín..."
          value={extra}
          maxLength={200}
          onChange={(e) => setExtra(e.target.value)}
          className="bg-slate-800/40 backdrop-blur-md border-white/10 text-slate-100 placeholder:text-slate-500 rounded-2xl min-h-[100px] focus-visible:ring-amber-400"
        />
        <p className={`text-xs mt-1.5 text-right ${extra.length >= 180 ? "text-amber-400" : "text-slate-500"}`}>
          {extra.length}/200
        </p>
      </section>

      {/* SUBMIT */}
      <div className="flex flex-col sm:flex-row gap-3 items-center justify-center pt-4">
        <Button
          onClick={submit}
          disabled={!canSubmit || loading}
          data-testid="generate-button"
          className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full px-10 py-6 text-lg shadow-[0_0_30px_rgba(251,191,36,0.45)] hover:shadow-[0_0_45px_rgba(251,191,36,0.7)] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          {loading ? "Tejiendo cuento..." : "Crear cuento mágico"}
        </Button>
        {!canSubmit && (
          <p className="text-sm text-slate-400" data-testid="hint-required">
            Elige al menos 1 personaje y 1 escenario
          </p>
        )}
      </div>
    </div>
  );
}
